import {debounceTime, filter, takeUntil} from 'rxjs/operators';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Budget, GeneratedBy, Material} from '../../../../interfaces';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DataService} from '../../../../services/data.service';
import {BudgetModel} from '../../../models/budget-model';
import {Subject} from 'rxjs';
import {SubBudgetModel} from '../../../models/sub-budget';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {ConfirmDialogComponent} from '../../../../private/private.component';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {AuthService} from '../../../../services/auth.service';
import {CommonModule} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';


@Component({
  selector: 'app-production-costs-form-v1',
  templateUrl: './production-costs-form-component-v1.component.html',
  styleUrls: ['./production-costs-form-component-v1.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule
  ]
})
export class ProductionCostsFormV1Component implements OnInit, OnDestroy {

  public operationInProgress = false;
  @Output() changeSelectedTab = new EventEmitter<string>();
  @Output() updateCostSummary = new EventEmitter<string>();
  public productionCostsForm: UntypedFormGroup;
  public exchangeRateHint: string;
  private unsubscribe$ = new Subject();

  constructor(private dataService: DataService,
              private fb: UntypedFormBuilder,
              private _snackbar: MatSnackBar,
              public dialog: MatDialog,
              private auth: AuthService) {
  }

  get isExport() {
    if (!this._budget) return false;
    if (this._budget.export) return true;
    return false;
  }

  private _budget: Budget;
  get budget() {
    return this._budget;
  }

  @Input() set budget(b: Budget) {
    this._budget = b;
    if (this.productionCostsForm) {
      this.productionCostsForm.patchValue({
        exchangeRate: this.isExport ? this._budget.export.exchangeRate : undefined
      }, {
        emitEvent: false
      });

      this.refreshFormEnabled();
    }
  }

  public canGrantProjectAuth = false;
  private canEditBudget = false;
  public canRefreshRate = false;
  private canEditOtherBudget = false;

  ngOnInit() {

    this.productionCostsForm = this.fb.group({
      exchangeRate: [this.isExport ? this._budget.export.exchangeRate : undefined, Validators.min(0)],
      design: [0, Validators.min(0)],
      shipping: [0, Validators.min(0)]
    });

    this.listenForSubBudgetMaterials();
    this.refreshFormEnabled();
    this.setPermissionHandler();
    /**
     * Auto save / commit enabled
     */
    this.productionCostsForm.valueChanges
      .pipe(debounceTime(1000),
        takeUntil(this.unsubscribe$))
      .subscribe(async result => {
        await this.submit();
      });
  }

  setPermissionHandler() {
    this.auth.userHasPermissions([
      'app.budget.auth.grant',
      'app.budget.write',
      'app.budget.edit.others'
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(val => {
        this.canGrantProjectAuth = val[1];
        this.canEditBudget = val[2];
        this.canEditOtherBudget = val[3];
        this.refreshFormEnabled();
      });
  }

  refreshFormEnabled() {
    if (!this.productionCostsForm) return;

    if (BudgetModel.genericEditBudgetPermission(
      this.budget,
      this.auth.currentUser,
      this.canEditBudget,
      this.canEditOtherBudget)) {
      this.productionCostsForm.enable({emitEvent: false});
    } else {
      this.productionCostsForm.disable({emitEvent: false});
    }

    this.canRefreshRate = BudgetModel.isRateEditable(
      this.budget,
      this.auth.currentUser,
      this.canGrantProjectAuth,
      this.canEditBudget,
      this.canEditOtherBudget);
  }

  public listenForSubBudgetMaterials() {

    this.dataService.getSBMaterial(this._budget.id, 'AUTO_BUDGET', 'design')
      .pipe(
        filter((mat) => mat != null),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((mat: Material) => {
        this.productionCostsForm.patchValue({
            design: mat.costEach
          },
          {
            emitEvent: false
          });
      });

    this.dataService.getSBMaterial(this._budget.id, 'AUTO_BUDGET', 'shipping')
      .pipe(
        filter((mat) => mat != null),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((mat: Material) => {
        this.productionCostsForm.patchValue({
            shipping: mat.costEach
          },
          {
            emitEvent: false
          });
      });
  }

  public ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  public refreshExchangeRate = () => {
    const dialogMessage = {confirmMessage: `Are you sure you want to refresh the exchange rate? This has impact on all the $ values`};
    const dialogData = {data: dialogMessage, height: '250px', width: '600px', disableClose: true};
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogData);

    dialogRef.afterClosed().subscribe(async choice => {
      if (choice.confirm) {
        this.exchangeRateHint = '';
        this.dataService.getExchangeRateInternal('USD')
          .subscribe((r) => {
            if (!r || !r.rates) {
              this._snackbar.open('Can not update rate. Something went wrong, please try again.', 'Close', {duration: 5000});
              return;
            }
            this._budget.export.exchangeRate = r.rates['USD'];
            this.dataService.patchBudget(this._budget.id, {export: this._budget.export});
            this.exchangeRateHint = 'Rate update from https://fixer.io';
          });
      }
    });
  }

  async submit() {

    try {
      this.operationInProgress = true;
      const batch = this.dataService.getBatch();

      if (this.isExport) {
        if (this._budget.export.exchangeRate !== this.productionCostsForm.value.exchangeRate) {
          this._budget.export.exchangeRate = this.productionCostsForm.value.exchangeRate;
          await this.dataService.patchBudget(this._budget.id, {export: this._budget.export});
        }

        await this.dataService.upsertSubBudget(this._budget.id, 'AUTO_BUDGET',
          new SubBudgetModel('Overseas', GeneratedBy.Export, 'AUTO_BUDGET', false), batch);

        const exportDesign = this.productionCostsForm.value.design;
        await this.dataService.setSBMaterial(this._budget.id, 'AUTO_BUDGET', 'design', {
          description: 'Overseas Design & Project Management',
          category: 'IUS',
          costEach: exportDesign,
          quantity: 1,
          unit: 'pieces'
        }, batch);

        const exportShipping = this.productionCostsForm.value.shipping;
        await this.dataService.setSBMaterial(this._budget.id, 'AUTO_BUDGET', 'shipping', {
          description: 'Overseas Shipping',
          category: 'SPE',
          costEach: exportShipping,
          quantity: 1,
          unit: 'pieces'
        }, batch);

        const data1 = await batch.commit();
        console.log('ProductionForm - Budget patched, force updating costSummary..', data1, this.isExport, this._budget.id);
      }
    } catch (err) {
      console.log(err);
    } finally {
      this.operationInProgress = false;
      this.updateCostSummary.emit(this._budget.id);
      console.log('ProductionForm - cost summary force updated for', this._budget.id);
    }

  }

  changeTab(label: string) {
    this.changeSelectedTab.emit(label);
  }
}
